import React, { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import { Copy, MoreHorizontal, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/ui_custom/user-avatar'
import { UserProps } from '@/entities/user'
import { MessageProps } from '@/entities/message'
import Loader from '@/components/ui_custom/loader'
import { toast } from '@/hooks/use-toast'
import { showConfirm } from '@/lib/alerts'
import { renderMessageContent } from '@/utils/render-message-content'
import { useMediaQuery } from '@/hooks/use-media-query'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

interface MessageBubbleProps {
  message?: MessageProps
  user?: UserProps | null
  onDelete?: (id: string) => void
  isLoadingMessage?: boolean
  isStreaming?: boolean
  streamedContent?: string
  isLast?: boolean
  onRegenerate?: () => void
}

const THINKING_PHRASES = [
  'Pensando…',
  'Analisando sua pergunta…',
  'Consultando a metodologia Mãe Nutrida…',
  'Buscando nas referências da Camila…',
  'Revisando os protocolos…',
  'Estudando seu caso…',
  'Cruzando informações clínicas…',
  'Conectando os pontos…',
  'Verificando as evidências…',
  'Organizando uma resposta cuidadosa…',
  'Avaliando individualizações possíveis…',
  'Checando contraindicações…',
]

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, user, onDelete, isLoadingMessage = false, isStreaming = false, streamedContent, isLast = false, onRegenerate }) => {
  const isCurrentUser = !isLoadingMessage && message?.senderId != null && message?.senderId != 0
  const formattedTime = dayjs(message?.createdAt)?.format('HH:mm')
  const isMdAndDown = useMediaQuery('(max-width: 768px)')

  const targetContent = isStreaming && streamedContent !== undefined ? streamedContent : (message?.content || '')

  const [displayedContent, setDisplayedContent] = useState(targetContent)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isStreaming) {
      setDisplayedContent(targetContent)
      return
    }
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      setDisplayedContent(prev => {
        if (prev.length >= targetContent.length) return targetContent
        const diff = targetContent.length - prev.length
        const advance = Math.max(4, Math.ceil(diff * 0.12))
        const next = targetContent.slice(0, Math.min(prev.length + advance, targetContent.length))
        if (next.length < targetContent.length) {
          rafRef.current = requestAnimationFrame(tick)
        }
        return next
      })
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      cancelled = true
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [targetContent, isStreaming])

  const renderedHtml = isCurrentUser ? null : renderMessageContent(displayedContent)
  const displayContent = displayedContent

  const isPendingNina = !isCurrentUser && message?.status === 'PROCESSING' && !displayContent
  const isThinking = isStreaming ? !displayContent : isPendingNina
  const [thinkingIdx, setThinkingIdx] = useState<number | null>(null)
  useEffect(() => {
    if (isThinking) {
      setThinkingIdx(prev => prev ?? Math.floor(Math.random() * THINKING_PHRASES.length))
    } else {
      setThinkingIdx(null)
    }
  }, [isThinking])

  const handleCopyMessage = (content: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (navigator?.clipboard?.writeText) {
      const plainText = isCurrentUser ? content : renderMessageContent(content).replace(/<[^>]*>?/g, '')

      navigator.clipboard.writeText(plainText || '')

      toast({
        variant: 'success',
        title: 'Sucesso',
        description: `Mensagem copiada para a área de transferência`
      })
    }
  }

  const handleRegenerateMessage = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const { isConfirmed } = await showConfirm('Regenerar resposta?')
      if (!isConfirmed) return
      onRegenerate?.()
      toast({
        variant: 'success',
        title: 'Gerando nova resposta...',
        description: 'A Nina está gerando uma nova resposta.',
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleDeleteMessage = async (ref: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!ref) return

    try {
      const { isConfirmed } = await showConfirm('Deseja deletar a mensagem?')
      if (!isConfirmed) return

      onDelete?.(ref)
      toast({
        variant: 'success',
        title: 'Sucesso',
        description: `Mensagem deletada com sucesso`
      })
    } catch (err) {
      console.error(err)
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: `Não foi possível deletar a mensagem`
      })
    }
  }

  return (
    <div className={cn('group flex w-full items-start gap-2 py-2', isCurrentUser ? 'justify-end' : 'justify-start')}>
      {!isCurrentUser && (
        <div className="flex flex-col items-center gap-1 shrink-0">
          <motion.div
            className="h-9 w-9 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-white ring-1 ring-primary/20 shadow-sm"
            style={{ background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 100%)' }}
            aria-label="Nina"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <Sparkles className="h-[18px] w-[18px]" strokeWidth={2.25} />
          </motion.div>
          {isMdAndDown && <span className="text-[11px] text-gray-500 dark:text-gray-400">{formattedTime}</span>}
        </div>
      )}

      {isLoadingMessage ? (
        <div className="py-2 flex items-start">
          <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-gray-100 dark:bg-gray-800 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_1px_3px_rgba(0,0,0,0.06)]">
            <Loader variant="dots" size="sm" />
          </div>
        </div>
      ) : (
        <div className={cn('py-2 flex flex-col min-w-0 flex-1', isCurrentUser ? 'items-end' : 'items-start')}>
          <div className={cn('relative max-w-[92%] md:max-w-[680px]')}>
            <div
              className={cn(
                'relative px-4 py-2.5 rounded-2xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_1px_3px_rgba(0,0,0,0.06)] transition-shadow duration-200 ease-out',
                isCurrentUser
                  ? 'bg-primary text-white rounded-tr-md'
                  : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white rounded-tl-md text-sm'
              )}
            >
              {isCurrentUser ? (
                <div className="whitespace-pre-wrap">{displayContent}</div>
              ) : isThinking ? (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic min-w-[220px]">
                  <span className="text-sm">{THINKING_PHRASES[thinkingIdx ?? 0]}</span>
                  <Loader variant="dots" size="sm" />
                </div>
              ) : (
                <div>
                  <div
                    className="message-content prose prose-sm dark:prose-invert max-w-none
                      prose-headings:font-semibold prose-headings:text-foreground
                      prose-p:my-2 prose-p:leading-relaxed
                      prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5
                      prose-strong:text-foreground prose-em:text-foreground
                      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                      prose-blockquote:border-l-4 prose-blockquote:border-primary
                      prose-blockquote:bg-primary/5 prose-blockquote:py-1 prose-blockquote:px-3
                      prose-blockquote:not-italic prose-blockquote:rounded-r-md
                      prose-blockquote:my-3 prose-blockquote:text-foreground/90
                      prose-table:my-3 prose-th:bg-muted prose-th:p-2 prose-td:p-2
                      prose-th:border prose-td:border prose-th:border-border prose-td:border-border
                      prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                      prose-code:text-sm prose-code:before:content-none prose-code:after:content-none"
                    dangerouslySetInnerHTML={{ __html: renderedHtml ?? '' }}
                  />
                  {isStreaming && (
                    <span
                      className="inline-block w-[2px] h-4 bg-current ml-1 align-baseline rounded-sm"
                      style={{ animation: 'nina-cursor-blink 1.1s ease-in-out infinite' }}
                      aria-hidden
                    />
                  )}
                </div>
              )}
            </div>

            {!isMdAndDown && !isThinking && !isStreaming && (
              <div
                className={cn(
                  'absolute bottom-0 opacity-0 translate-y-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-y-0 pointer-events-none group-hover:pointer-events-auto',
                  isCurrentUser ? '-left-16' : '-right-16'
                )}
              >
                <div className="flex items-center gap-0.5 rounded-lg bg-background/80 backdrop-blur-sm border border-border/60 shadow-sm p-0.5">
                  <Button
                    variant="link"
                    onClick={e => handleDeleteMessage(message?.ref || '', e)}
                    className="h-7 w-7 p-0 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors duration-150 ease-out"
                    aria-label="Excluir mensagem"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="link"
                    onClick={e => handleCopyMessage(message?.content || '', e)}
                    className="h-7 w-7 p-0 rounded-md text-gray-500 hover:bg-primary/10 hover:text-primary dark:text-gray-400 dark:hover:bg-primary/20 dark:hover:text-primary transition-colors duration-150 ease-out"
                    aria-label="Copiar mensagem"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {!isCurrentUser && isLast && onRegenerate && (
                    <Button
                      variant="link"
                      onClick={e => handleRegenerateMessage(e)}
                      className="h-7 w-7 p-0 rounded-md text-gray-500 hover:bg-primary/10 hover:text-primary dark:text-gray-400 dark:hover:bg-primary/20 dark:hover:text-primary transition-colors duration-150 ease-out"
                      aria-label="Regenerar resposta"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {isMdAndDown && !isThinking && !isStreaming && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'mt-1 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground/70 hover:text-foreground hover:bg-accent transition-colors duration-150 ease-out',
                    isCurrentUser ? 'self-end' : 'self-start'
                  )}
                  aria-label="Ações da mensagem"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isCurrentUser ? 'end' : 'start'} className="min-w-[140px]">
                <DropdownMenuItem onClick={e => handleCopyMessage(message?.content || '', e as unknown as React.MouseEvent)} className="gap-2 text-sm cursor-pointer">
                  <Copy className="h-3.5 w-3.5" /> Copiar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={e => handleDeleteMessage(message?.ref || '', e as unknown as React.MouseEvent)} className="gap-2 text-sm text-red-600 focus:text-red-600 cursor-pointer">
                  <Trash2 className="h-3.5 w-3.5" /> Excluir
                </DropdownMenuItem>
                {!isCurrentUser && isLast && onRegenerate && (
                  <DropdownMenuItem onClick={e => handleRegenerateMessage(e as unknown as React.MouseEvent)} className="gap-2 text-sm cursor-pointer">
                    <RefreshCw className="h-3.5 w-3.5" /> Regenerar resposta
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {!isMdAndDown && <span className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">{formattedTime}</span>}
        </div>
      )}

      {isCurrentUser && user && (
        <div className="flex flex-col items-center gap-1 shrink-0">
          <motion.div
            className="shadow-sm rounded-full"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <UserAvatar name={user.name} size="sm" />
          </motion.div>
          {isMdAndDown && <span className="text-[11px] text-gray-500 dark:text-gray-400">{formattedTime}</span>}
        </div>
      )}
    </div>
  )
}
